import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';

/**
 * DateContext - Global state management for date range across the application
 * 
 * Provides shared date state (fromDate, toDate) that persists across page navigation
 * Default values:
 * - fromDate: January 1, 2025
 * - toDate: Current date
 */

interface DateContextType {
  fromDate: Date;
  toDate: Date;
  setFromDate: (date: Date) => void;
  setToDate: (date: Date) => void;
  selectedMachineName: string;
  setSelectedMachineName: (name: string) => void;
}

// Create the context
const DateContext = createContext<DateContextType | undefined>(undefined);

/**
 * DateProvider - Wraps the application to provide global date state
 */
export const DateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Initialize with default dates
  const [fromDate, setFromDate] = useState(new Date('2026-03-01'));
  const [toDate, setToDate] = useState(new Date());
  const [selectedMachineName, setSelectedMachineName] = useState('');

  const value = useMemo(() => ({
    fromDate,
    toDate,
    setFromDate,
    setToDate,
    selectedMachineName,
    setSelectedMachineName,
  }), [fromDate, toDate, selectedMachineName]);

  return <DateContext.Provider value={value}>{children}</DateContext.Provider>;
};

/**
 * useDateContext - Custom hook to access date context
 * Must be used within DateProvider
 */
export const useDateContext = (): DateContextType => {
  const context = useContext(DateContext);
  
  if (context === undefined) {
    throw new Error('useDateContext must be used within a DateProvider');
  }
  
  return context;
};

export default DateContext;
